import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { SearchIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot='command'
      className={cn(
        'hc:bg-popover hc:text-popover-foreground hc:flex hc:h-full hc:w-full hc:flex-col hc:overflow-hidden hc:rounded-md',
        className
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className='sr-only'>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn('hc:overflow-hidden hc:p-0', className)}
        showCloseButton={showCloseButton}>
        <Command className='hc:[&_[cmdk-group-heading]]:text-muted-foreground hc:**:data-[slot=command-input-wrapper]:h-12 hc:[&_[cmdk-group-heading]]:px-2 hc:[&_[cmdk-group-heading]]:font-medium hc:[&_[cmdk-group]]:px-2 hc:[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 hc:[&_[cmdk-input-wrapper]_svg]:h-5 hc:[&_[cmdk-input-wrapper]_svg]:w-5 hc:[&_[cmdk-input]]:h-12 hc:[&_[cmdk-item]]:px-2 hc:[&_[cmdk-item]]:py-3 hc:[&_[cmdk-item]_svg]:h-5 hc:[&_[cmdk-item]_svg]:w-5'>
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot='command-input-wrapper'
      className='hc:flex hc:h-9 hc:items-center hc:gap-2 hc:border-b hc:border-input hc:px-3'>
      <SearchIcon className='hc:size-4 hc:shrink-0 hc:opacity-50' />
      <CommandPrimitive.Input
        data-slot='command-input'
        className={cn(
          'hc:placeholder:text-muted-foreground hc:flex hc:h-10 hc:w-full hc:rounded-md hc:bg-transparent hc:py-3 hc:text-sm hc:outline-hidden hc:disabled:cursor-not-allowed hc:disabled:opacity-50',
          className
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot='command-list'
      className={cn(
        'hc:max-h-[300px] hc:scroll-py-1 hc:overflow-x-hidden hc:overflow-y-auto',
        className
      )}
      {...props}
    />
  );
}

function CommandEmpty({ ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot='command-empty'
      className='hc:py-6 hc:text-center hc:text-sm'
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot='command-group'
      className={cn(
        'hc:text-foreground hc:[&_[cmdk-group-heading]]:text-muted-foreground hc:overflow-hidden hc:p-1 hc:[&_[cmdk-group-heading]]:px-2 hc:[&_[cmdk-group-heading]]:py-1.5 hc:[&_[cmdk-group-heading]]:text-xs hc:[&_[cmdk-group-heading]]:font-medium',
        className
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot='command-separator'
      className={cn('hc:bg-border hc:-mx-1 hc:h-px', className)}
      {...props}
    />
  );
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot='command-item'
      className={cn(
        "hc:data-[selected=true]:bg-accent hc:data-[selected=true]:text-accent-foreground hc:[&_svg:not([class*='text-'])]:text-muted-foreground hc:relative hc:flex hc:cursor-default hc:items-center hc:gap-2 hc:rounded-sm hc:px-2 hc:py-1.5 hc:text-sm hc:outline-hidden hc:select-none hc:data-[disabled=true]:pointer-events-none hc:data-[disabled=true]:opacity-50 hc:[&_svg]:pointer-events-none hc:[&_svg]:shrink-0 hc:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='command-shortcut'
      className={cn('hc:text-muted-foreground hc:ml-auto hc:text-xs hc:tracking-widest', className)}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
